import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useShippingRate } from '@/features/shipping/hooks/use-shipping-rate';
import { useDeleteShippingRate } from '@/features/shipping/hooks/use-delete-shipping-rate';
import { useCountriesMap } from '@/lib/hooks/use-countries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Switch } from '@/components/atoms/switch';
import { DeleteConfirmDialog } from '@/components/molecules/delete-confirm-dialog';
import { ArrowLeft, Truck, Package, DollarSign, Gift, Clock, Zap, MapPin, Pencil } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatPrice, convertFromEur, getAmountFromPriceObject } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { toast } from 'sonner';
import type { ShippingRateType, DeliveryType } from '@/features/shipping/types/shipping.types';

const typeIconMap: Record<ShippingRateType, React.ElementType> = {
  flat: Truck,
  weight_based: Package,
  price_based: DollarSign,
  free_shipping: Gift,
};

const deliveryTypeIconMap: Record<DeliveryType, React.ElementType> = {
  standard: Truck,
  express: Zap,
  same_day: Clock,
  pickup: MapPin,
};

export default function ShippingRateDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: rate, isLoading, error } = useShippingRate(id ?? '');
  const deleteShippingRate = useDeleteShippingRate();
  const { getCountryFlag, getCountryName } = useCountriesMap();
  const { currency } = useConfigStore();

  const handleDelete = () => {
    if (!rate) return;
    deleteShippingRate.mutate(rate.refId, {
      onSuccess: () => {
        toast.success(t('shipping.rates.deleteSuccess', 'Tarif de livraison supprimé avec succès'));
        navigate('/shipping');
      },
      onError: () => {
        toast.error(t('shipping.rates.deleteError', 'Erreur lors de la suppression du tarif'));
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !rate) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('shipping.error')}</p>
      </div>
    );
  }

  const rateType = rate.rateCalculation?.type || 'flat';
  const TypeIcon = typeIconMap[rateType] || Truck;
  const DeliveryIcon = deliveryTypeIconMap[rate.deliveryType] || Truck;
  const priceValue = parseFloat(rate.price) || 0;
  const isFreeShipping = rateType === 'free_shipping' || priceValue === 0;

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/shipping">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-md bg-muted p-3 shrink-0">
              <TypeIcon className="size-6 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold truncate">{rate.name}</h1>
              {rate.description && (
                <p className="text-muted-foreground truncate">{rate.description}</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('shipping.rates.active')}</span>
            <Switch checked={rate.isActive} />
          </div>
          <Link to={`/shipping/rates/${id}/edit`}>
            <Button variant="outline" size="sm">
              <Pencil className="size-4 mr-2" />
              {t('common.edit')}
            </Button>
          </Link>
          <DeleteConfirmDialog
            itemName={rate.name}
            onConfirm={handleDelete}
            isLoading={deleteShippingRate.isPending}
            variant="button"
          />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('shipping.rates.pricing')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('shipping.rates.type')}</span>
              <Badge variant="outline">
                {t(`shipping.rates.types.${rateType}`, { defaultValue: rateType })}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('shipping.rates.rate')}</span>
              <span className="text-2xl font-bold">
                {isFreeShipping ? t('shipping.rates.free') : formatPrice(getAmountFromPriceObject(rate.prices, currency, priceValue), currency)}
              </span>
            </div>
            {rate.currency && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('shipping.rates.currency')}</span>
                <span>{rate.currency}</span>
              </div>
            )}
            {rate.freeShippingThreshold && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('shipping.rates.freeThreshold')}</span>
                <span>{formatPrice(convertFromEur(parseFloat(rate.freeShippingThreshold), currency), currency)}</span>
              </div>
            )}
            {rate.prices && (
              <div className="pt-4 border-t space-y-2">
                <p className="text-sm font-medium">{t('shipping.rates.pricesByRegion')}</p>
                {rate.prices.comoros !== undefined && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Comoros (KMF)</span>
                    <span>{formatPrice(rate.prices.comoros, 'KMF')}</span>
                  </div>
                )}
                {rate.prices.usd !== undefined && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">USD</span>
                    <span>{formatPrice(rate.prices.usd, 'USD')}</span>
                  </div>
                )}
                {rate.prices.euro !== undefined && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">EUR</span>
                    <span>{formatPrice(rate.prices.euro, 'EUR')}</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('shipping.rates.delivery')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('shipping.rates.deliveryType')}</span>
              <div className="flex items-center gap-2">
                <DeliveryIcon className="size-4" />
                <span>{t(`shipping.rates.deliveryTypes.${rate.deliveryType}`, { defaultValue: rate.deliveryType })}</span>
              </div>
            </div>
            {rate.estimatedDeliveryDays && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('shipping.rates.estimatedDays')}</span>
                <span>
                  {rate.estimatedDeliveryDays}
                  {rate.maxDeliveryDays && rate.maxDeliveryDays !== rate.estimatedDeliveryDays
                    ? `-${rate.maxDeliveryDays}`
                    : ''} {t('shipping.rates.days')}
                </span>
              </div>
            )}
            {rate.deliveryCompany && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('shipping.rates.deliveryCompany')}</span>
                <span>{rate.deliveryCompany}</span>
              </div>
            )}
            {rate.maxWeight && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('shipping.rates.maxWeight')}</span>
                <span>{rate.maxWeight}</span>
              </div>
            )}
            {rate.maxDimensions && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('shipping.rates.maxDimensions')}</span>
                <span>{rate.maxDimensions}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {rate.availableCountries && rate.availableCountries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('shipping.rates.availableCountries')} ({rate.availableCountries.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {rate.availableCountries.map((countryCode) => {
                const flag = getCountryFlag(countryCode);
                const name = getCountryName(countryCode);
                return (
                  <Badge key={countryCode} variant="secondary" className="gap-1.5 py-1.5">
                    {flag && <span className="text-base">{flag}</span>}
                    <span>{name}</span>
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {rate.availableRegions && rate.availableRegions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('shipping.rates.availableRegions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {rate.availableRegions.map((region) => (
                <Badge key={region} variant="outline">
                  {region}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {rate.rateCalculation && (
        <Card>
          <CardHeader>
            <CardTitle>{t('shipping.rates.calculation')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {rate.rateCalculation.baseRate !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('shipping.rates.baseRate')}</span>
                <span>{formatPrice(getAmountFromPriceObject(rate.rateCalculation.baseRates, currency, rate.rateCalculation.baseRate), currency)}</span>
              </div>
            )}
            {rate.rateCalculation.weightUnit && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('shipping.rates.weightUnit')}</span>
                <span>{rate.rateCalculation.weightUnit}</span>
              </div>
            )}
            {rate.rateCalculation.weightRanges && rate.rateCalculation.weightRanges.length > 0 && (
              <div className="pt-4 border-t">
                <p className="text-sm font-medium mb-3">{t('shipping.rates.weightRanges')}</p>
                <div className="space-y-2">
                  {rate.rateCalculation.weightRanges.map((range, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {range.minWeight} - {range.maxWeight} {rate.rateCalculation.weightUnit || 'kg'}
                      </span>
                      <span>{formatPrice(getAmountFromPriceObject(range.rates, currency, range.rate), currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {rate.rateCalculation.priceRanges && rate.rateCalculation.priceRanges.length > 0 && (
              <div className="pt-4 border-t">
                <p className="text-sm font-medium mb-3">{t('shipping.rates.priceRanges')}</p>
                <div className="space-y-2">
                  {rate.rateCalculation.priceRanges.map((range, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {formatPrice(range.minPrice, currency)} - {formatPrice(range.maxPrice, currency)}
                      </span>
                      <span>{formatPrice(getAmountFromPriceObject(range.rates, currency, range.rate), currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
